import { Avatar, Grid, makeStyles, Typography } from '@material-ui/core';
import React from 'react';
import {
  BlockedEnvironmentsFields,
  EnvironmentHealthFields,
  InstanceStatusFields,
  InstancesWithSessionTimeoutFields,
  EnvironmentsWithFailedZeroTrustResponse,
} from 'modules/monitor/interfaces';
import AutoCard from 'components/AutoCard';
import Button from 'components/Button';
import VisibilityIcon from '@material-ui/icons/Visibility';
import GetAppIcon from '@material-ui/icons/GetApp';
import StyledTypography from 'components/StyledTypography';
import i18n from 'i18n';
import { connect } from 'react-redux';
import { modalOpen, modalClose } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import { CompanyData, RolesEnum } from 'modules/authenticate/interfaces';
import ApplicationState from 'store/interfaces';
import clsx from 'clsx';
import styles, { overrideCard } from './styles';
import BlockedEnvironmentsTable from '../BlockedEnvironmentsTable';
import EnvironmentHealthTable from '../EnvironmentHealthTable';
import InstanceStatusTable from '../InstanceStatusTable';
import InstancesWithSessionTimeoutTable from '../InstancesWithSessionTimeoutTable';
import { MonitorCardsEnum, generatePDF } from './helpers';
import EnvironmentsWithFailedZeroTrustCard from './EnvironmentsWithFailedZeroTrustCard';

const useStyles = makeStyles(styles);

export interface SOPCardItem {
  title: string;
  value: string | number;
  key: string;
  onClick: () => void;
}

interface SOPCardProps {
  blockedEnvironmentsData: BlockedEnvironmentsFields;
  environmentHealthData: EnvironmentHealthFields;
  instanceStatusData: InstanceStatusFields;
  instancesWithSessionTimeout: InstancesWithSessionTimeoutFields;
  environmentsWithFailedZeroTrust: EnvironmentsWithFailedZeroTrustResponse;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  companySelected: CompanyData | null;
  role?: RolesEnum;
}

const SOPCard: React.FC<SOPCardProps> = ({
  blockedEnvironmentsData,
  environmentHealthData,
  instanceStatusData,
  instancesWithSessionTimeout,
  environmentsWithFailedZeroTrust,
  modalOpen,
  modalClose,
  companySelected,
  role,
}) => {
  const classes = useStyles();

  const blockedEnvironmentsCount = blockedEnvironmentsData?.count;
  const environmentHealthCount =
    environmentHealthData?.environmentToAnalyzeCount;
  const instanceStatusCount = instanceStatusData?.count;
  const instancesWithSessionTimeoutCount = instancesWithSessionTimeout?.count;

  const handleOpenModal = () => {
    modalOpen({
      title: i18n.t('MONITOR.ENVIRONMENTS_WITH_FAILED_ZERO_TRUST'),
      maxWidth: 'lg',
      content: <EnvironmentsWithFailedZeroTrustCard />,
      genericFooter: {},
    });
  };

  const handleOpenInstanceToAnalyze = () => {
    modalOpen({
      title: i18n.t('MONITOR.CARD_INSTANCES'),
      maxWidth: 'md',
      fullWidth: true,
      content: (
        <>
          <InstanceStatusTable instanceStatusData={instanceStatusData} />
          <InstancesWithSessionTimeoutTable />
        </>
      ),
      footer: (
        <Grid>
          <Button
            id="generate-pdf-button"
            submitButton
            startIcon={<GetAppIcon />}
            onClick={() => {
              generatePDF(instanceStatusData, MonitorCardsEnum.instanceProblem);
              generatePDF(
                instancesWithSessionTimeout,
                MonitorCardsEnum.instanceSessionTimeoutProblem
              );
              modalClose();
            }}
          >
            {i18n.t('GENERATE_PDF')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CLOSE_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleOpenBlockedEnvironmentsCount = () => {
    modalOpen({
      title: i18n.t('MONITOR.CARD_BLOCKED_ENVIRONMENTS'),
      maxWidth: 'lg',
      content: (
        <BlockedEnvironmentsTable
          blockedEnvironmentsData={blockedEnvironmentsData}
        />
      ),
      footer: (
        <Grid>
          <Button
            id="generate-pdf-button"
            submitButton
            startIcon={<GetAppIcon />}
            onClick={() => {
              generatePDF(
                blockedEnvironmentsData,
                MonitorCardsEnum.environmentBlock
              );
              modalClose();
            }}
          >
            {i18n.t('GENERATE_PDF')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CLOSE_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleOpenEnvironmentHealthCount = () => {
    modalOpen({
      title: i18n.t('MONITOR.CARD_ENVIRONMENT_HEALTH'),
      maxWidth: 'lg',
      content: (
        <EnvironmentHealthTable environmentHealthData={environmentHealthData} />
      ),
      footer: (
        <Grid>
          <Button
            id="generate-pdf-button"
            submitButton
            startIcon={<GetAppIcon />}
            onClick={() => {
              generatePDF(
                environmentHealthData,
                MonitorCardsEnum.environmentMissing
              );
              modalClose();
            }}
          >
            {i18n.t('GENERATE_PDF')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CLOSE_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const sopData: SOPCardItem[] = [
    {
      key: MonitorCardsEnum.environmentMissing,
      title: i18n.t('MONITOR.CARD_ENVIRONMENT_HEALTH'),
      value: environmentHealthCount,
      onClick: handleOpenEnvironmentHealthCount,
    },
    {
      key: MonitorCardsEnum.instanceProblem,
      title: i18n.t('MONITOR.CARD_INSTANCES'),
      value: instanceStatusCount + instancesWithSessionTimeoutCount,
      onClick: handleOpenInstanceToAnalyze,
    },
    {
      key: MonitorCardsEnum.environmentBlock,
      title: i18n.t('MONITOR.CARD_BLOCKED_ENVIRONMENTS'),
      value: blockedEnvironmentsCount,
      onClick: handleOpenBlockedEnvironmentsCount,
    },
    {
      key: MonitorCardsEnum.environmentsWithFailedZeroTrust,
      title: i18n.t('MONITOR.ENVIRONMENTS_WITH_FAILED_ZERO_TRUST'),
      value: environmentsWithFailedZeroTrust?.total_count,
      onClick: handleOpenModal,
    },
  ];

  return (
    <AutoCard overrideClasses={overrideCard()}>
      <Grid container xs={10} className={classes.container}>
        {sopData?.map(item => (
          <Grid container className={classes.item} key={item.key}>
            <Grid item className={classes.line}>
              <Typography className={classes.title}>{item?.title}</Typography>
            </Grid>

            <Grid item className={classes.line}>
              <StyledTypography value={item?.value} />
              <Avatar
                variant="rounded"
                onClick={item?.onClick}
                className={clsx(classes.avatar, {
                  [classes.avatarGreen]:
                    role === RolesEnum.admin && !companySelected,
                  [classes.avatarGray]:
                    role === RolesEnum.superAdmin && !companySelected,
                  [classes.avatarBlue]:
                    role === RolesEnum.operator && !companySelected,
                  [classes.avatarYellow]: companySelected,
                })}
              >
                <VisibilityIcon className={classes.icon} />
              </Avatar>
            </Grid>
          </Grid>
        ))}
      </Grid>
    </AutoCard>
  );
};

const mapStateToProps = ({
  monitor: {
    blockedEnvironmentsData,
    environmentHealthData,
    instanceStatusData,
    instancesWithSessionTimeout,
    environmentsWithFailedZeroTrust,
  },
  authenticate: { companySelected, data },
}: ApplicationState) => ({
  companySelected,
  blockedEnvironmentsData,
  environmentHealthData,
  instanceStatusData,
  instancesWithSessionTimeout,
  environmentsWithFailedZeroTrust,
  role: data?.role,
});

export default connect(mapStateToProps, { modalOpen, modalClose })(SOPCard);
